import { ComponentProps, useState } from 'react';
import clsx from 'clsx';
import { DATE_TIME_FORMAT, TIME_FORMAT } from 'constants/dates';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { generateRangePickerPresets } from './generateRangePickerPresets';

import './OpRangePicker.scss';

const { RangePicker } = DatePicker;

type RangePickerProps = ComponentProps<typeof RangePicker>;

interface IOpRangePickerPropsType
  extends Omit<RangePickerProps, 'value' | 'onChange' | 'presets'> {
  className?: string;
  showTime?: boolean;
  showPresets?: boolean;
  presets?: () => RangePickerProps['presets'];
  value?: (string | null)[] | null;
  onChange?: (value: (string | null)[] | null, formatString: string[]) => void;
}

export const OpRangePicker = ({
  className,
  onChange,
  onClick,
  value,
  showPresets = true,
  presets,
  ...rangePickerProps
}: IOpRangePickerPropsType) => {
  const [finalPresets, setFinalPresets] = useState(
    showPresets ? presets?.() || generateRangePickerPresets() : undefined,
  );

  // We want to store array of strings in the form values rather than array of dayjs objects
  const handleChange: RangePickerProps['onChange'] = (values, formatString) => {
    onChange?.(
      values ? values?.map((v) => (v ? v.toISOString() : null)) : null,
      formatString,
    );
  };

  const handleClick: RangePickerProps['onClick'] = (event) => {
    onClick?.(event);
    setFinalPresets(
      showPresets ? presets?.() || generateRangePickerPresets() : undefined,
    );
  };

  // We need to convert the now stored array of strings to array dayjs objects
  const finalValue = value
    ? value.map((v) => (v ? dayjs(v) : undefined))
    : undefined;

  return (
    <RangePicker
      className={clsx('op-range-picker', className)}
      changeOnBlur
      showTime={{ format: TIME_FORMAT }}
      format={DATE_TIME_FORMAT}
      value={finalValue as RangePickerProps['value']}
      onChange={handleChange}
      onClick={handleClick}
      presets={finalPresets}
      {...rangePickerProps}
    />
  );
};
